import moment from 'moment';
import { createSelector, Selector } from '@reduxjs/toolkit';

import { IAllocationDisplayItem } from '../../../../components/video/ManageMinutesDrawer/interfaces';
import { RootState } from '../../../rootReducer';
import { addEndDateOneDayLessToNewerTerm, sortByStartDateTheByTypeThenByCreatedAt } from './selectors.utils';
import { IAllocationItemStore, ITermStartData } from './interfaces';

type Self = RootState['accounts']['video']['management'];

const selectSelf = (state: RootState): Self => state.accounts.video.management;

export const selectAllocationItemsIds = (state:RootState): Self['idsList'] => selectSelf(state).idsList;

export const selectMapOfAllocationItems = (state:RootState): Self['map'] => selectSelf(state).map;

export const selectAllocationItemsList: Selector<RootState, IAllocationDisplayItem[]> = createSelector(
  [selectAllocationItemsIds, selectMapOfAllocationItems],
  (ids, dictionary) => {
    const items: IAllocationItemStore[] = ids.map((id) => {
      return dictionary[id];
    });

    const itemsSorted = sortByStartDateTheByTypeThenByCreatedAt(items);
    const [firstItem] = itemsSorted;
    if(!firstItem) {
      return [];
    }

    const itemsWithTermEndDateWhenAvailable = addEndDateOneDayLessToNewerTerm(itemsSorted);

    const itemsWithDisplayData: IAllocationDisplayItem[] = itemsWithTermEndDateWhenAvailable.map((sortedItemWithEndDate) => {
      const { termStartDate, termEndDate, allocatedSeconds, updatedAt } = sortedItemWithEndDate;
      const startDateStrTrimmed = termStartDate.substring(0, termStartDate.indexOf('T'));
      const displayStartDateStr = moment(startDateStrTrimmed).format('MMM D, YYYY');
      const displayEndDateStr = termEndDate ? moment(termEndDate.substring(0, termEndDate.indexOf('T'))).format('MMM D, YYYY') : 'present';
      const displayUpdatedAtStr = moment(updatedAt).format('MMM D, YYYY');

      return {
        ...sortedItemWithEndDate,
        display: {
          belongsToCurrentTerm: !termEndDate,
          minutes: Number(allocatedSeconds / 60).toFixed(0),
          startDate: startDateStrTrimmed,
          termRange: [displayStartDateStr, displayEndDateStr].join(' - '),
          updatedAt: displayUpdatedAtStr
        }
      };
    });

    return itemsWithDisplayData;
  }
);

export const selectListFetchingStatus = (state:RootState): Self['fetchingManagementListStatus'] => selectSelf(state).fetchingManagementListStatus;

export const selectIsDrawerOpen = (state:RootState): Self['isModalOpen'] => selectSelf(state).isModalOpen;

export const selectAddMinutesRequestStatus = (state:RootState): Self['addMinutesToCurrentTermStatus'] => selectSelf(state).addMinutesToCurrentTermStatus;

export const selectCreateNewTermRequestStatus = (state:RootState): Self['creatingNewTermStatus'] => selectSelf(state).creatingNewTermStatus;

export const selectDeleteTermRequestStatus = (state:RootState): Self['deleteTermStatus'] => selectSelf(state).deleteTermStatus;

export const selectUpdateTermRequestStatus = (state:RootState): Self['updateTermStatus'] => selectSelf(state).updateTermStatus;

// TODO: testing
export const selectDataOfLastTwoStartPeriods: Selector<RootState, [ITermStartData | null, ITermStartData | null]> = createSelector(
  [selectAllocationItemsList],
  (itemsWithDisplayData) => {
    const firstIndex = itemsWithDisplayData.findIndex((item) => {
      return item.type === 'term';
    });

    if(firstIndex === -1) {
      return [null, null];
    }

    const secondIndex = itemsWithDisplayData.findIndex((item, index) => {
      return item.type === 'term' && index > firstIndex;
    });

    if(secondIndex === -1) {
      return [
        {
          index: firstIndex,
          item: itemsWithDisplayData[firstIndex]
        },
        null
      ];
    }

    return [
      {
        index: firstIndex,
        item: itemsWithDisplayData[firstIndex]
      },
      {
        index: secondIndex,
        item: itemsWithDisplayData[secondIndex]
      }
    ];
  }
);
